import * as React from "react"
import * as Yup from "yup"
import Form, { FormElement } from "@modules/form/components/Form"
import { MdArrowForward } from "react-icons/md"
import {
  Button,
  CheckboxConnectedField,
  InputConnectedField,
  StyledModalActions,
  Text,
  TextAreaConnectedField,
} from "gatsby-interface"
import {
  fastlyIntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.js"
import { useConnectFastlyToSiteMutation } from "@modules/cdn/fastly/queries.generated"
import { CdnIntegrationSharedProps } from "@modules/cdn/shared/types"

type FormValues = {
  accessToken: string
  serviceName: string
  hostname: string
  forceSSL: boolean
  tlsKey: string
  tlsCert: string
}

const validationSchema = Yup.object().shape<FormValues>({
  accessToken: Yup.string().required(uiText.messages.validationIsRequired),
  serviceName: Yup.string().required(uiText.messages.validationIsRequired),
  hostname: Yup.string().required(uiText.messages.validationIsRequired),
  forceSSL: Yup.boolean(),
  tlsKey: Yup.string(),
  tlsCert: Yup.string(),
})

export type FastlyIntegrationFormProps = CdnIntegrationSharedProps

export function FastlyIntegrationForm({
  siteId,
  integration,
  onSetCDNConfig,
  onSubmitFailed,
  onCancel,
}: FastlyIntegrationFormProps) {
  const [mutate] = useConnectFastlyToSiteMutation()

  return (
    <Form<FormValues>
      validationSchema={validationSchema}
      initialValues={{
        accessToken: integration?.config?.accessToken ?? ``,
        serviceName: integration?.config?.serviceName ?? ``,
        hostname: integration?.config?.hostname ?? ``,
        forceSSL: integration?.config?.forceSSL ?? false,
        tlsKey: integration?.config?.tlsKey ?? ``,
        tlsCert: integration?.config?.tlsCert ?? ``,
      }}
      onSubmit={values => {
        mutate({
          variables: {
            siteId,
            config: values,
          },
        })
          .then(({ data }) => {
            return onSetCDNConfig({
              success: data?.connectFastlyToSite?.success ?? false,
              errorType: data?.connectFastlyToSite?.errorType,
            })
          })
          .catch(onSubmitFailed)
      }}
    >
      {({ isSubmitting }) => (
        <FormElement applySpacing noValidate>
          <Text css={{ margin: 0 }}>{text.messages.introText}:</Text>
          <InputConnectedField
            label={text.labels.fastlyToken}
            name="accessToken"
            required
          />
          <InputConnectedField
            label={text.labels.fastlyServiceName}
            name="serviceName"
            required
          />
          <InputConnectedField
            label={text.labels.fastlyHostName}
            name="hostname"
            required
          />
          <CheckboxConnectedField
            label={text.labels.fastlyForceSSL}
            name="forceSSL"
          />

          <TextAreaConnectedField
            label={text.labels.fastlyTlsKey}
            name="tlsKey"
          />
          <TextAreaConnectedField
            label={text.labels.fastlyTlsCert}
            name="tlsCert"
          />

          <StyledModalActions>
            <Button variant="SECONDARY" type="reset" onClick={onCancel}>
              {uiText.actions.cancel}
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              rightIcon={<MdArrowForward />}
            >
              {uiText.actions.save}
            </Button>
          </StyledModalActions>
        </FormElement>
      )}
    </Form>
  )
}
